import React, { useRef, useState, useEffect } from "react";
import dayjs from "dayjs";
import { IconButton, Tooltip, Snackbar, Alert } from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import { CAREPMS_UPLOAD_HISTORY_ENDPOINT } from "../config/urls";
import AppTable from "../common/app-table";
import AppChip from "../common/app-chip";
import UploadHistoryDeleteModal from "./UploadHistoryDeleteModal";

const getTableColumns = (onDeleteClick, isAdmin) => {
  const columns = [
    { id: "upload_date",  label: "Upload Date",   minWidth: 120 },
    { id: "file_type",    label: "File Type",      minWidth: 100, render: (data) => data.filetype.name },
    { id: "holding_date", label: "Holdings Date",  minWidth: 140 },
    { id: "file_name",    label: "File Name",      minWidth: 220 },
    { id: "uploaded_by",  label: "Uploaded By",    minWidth: 160, render: (data) => data.user?.name   },
    {
      id: "status",
      label: "Status",
      minWidth: 110,
      render: (data) => (
        <AppChip
          variant="outlined"
          color={data.status === "success" ? "primary" : "error"}
          label={data.status === "success" ? "success" : "error"}
        />
      ),
    },
    { id: "message", label: "Message", minWidth: 300 },
  ];

  if (isAdmin) {
    columns.push({
      id: "actions",
      label: "Actions",
      minWidth: 80,
      render: (data) => {
        if (data.filetype?.name?.toLowerCase() === "clients") return null;
        return (
          <Tooltip title="Delete">
            <IconButton
              size="small"
              color="error"
              onClick={(e) => {
                e.stopPropagation();
                onDeleteClick(data);
              }}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        );
      },
    });
  }

  return columns;
};

export default function UploadHistoryTable({
  refreshKey,
  dateInput,
  fileType,
  status,
  uploadedBy,
  holdingDate,
  fileName,
  fromDate,
  toDate,
}) {
  const userPermissions = JSON.parse(localStorage.getItem("permissions") || "[]");
  const isAdmin = userPermissions.includes("upload.view");

  const [rowCount, setRowCount]                   = useState(0);
  const [uploadHistoryData, setUploadHistoryData] = useState([]);
  const [loading, setLoading]                     = useState(false);
  const [controller, setController]               = useState({ page: 0, rowsPerPage: 20 });
  const [refreshTick, setRefreshTick]             = useState(0);
  const [deleteModalOpen, setDeleteModalOpen]     = useState(false);
  const [selectedRowForDelete, setSelectedRowForDelete] = useState(null);
  const [snackbar, setSnackbar]                   = useState({ open: false, message: "", severity: "success" });
  const prevFilterKeyRef = useRef("");

  useEffect(() => {
    const normalizedUploadedBy = uploadedBy?.trim() ?? "";
    const normalizedFileName = fileName?.trim() ?? "";
    const filterKey = [
      dateInput ? dayjs(dateInput).format("YYYY-MM-DD") : "",
      fileType ?? "",
      status ?? "",
      normalizedUploadedBy,
      holdingDate ? dayjs(holdingDate).format("YYYY-MM-DD") : "",
      normalizedFileName,
      fromDate ? dayjs(fromDate).format("YYYY-MM-DD") : "",
      toDate ? dayjs(toDate).format("YYYY-MM-DD") : "",
    ].join("|");

    const shouldResetPage = prevFilterKeyRef.current !== filterKey && controller.page !== 0;
    prevFilterKeyRef.current = filterKey;

    const fetchData = async () => {
      setLoading(true);
      try {
        const tenant = localStorage.getItem("tenant");

        const params = new URLSearchParams();

        params.append("limit", controller.rowsPerPage);
        params.append("offset", controller.page * controller.rowsPerPage);

        if (dateInput)
          params.append("search_date", dayjs(dateInput).format("YYYY-MM-DD"));

        if (fileType)
          params.append("file_type", fileType);

        if (status)
          params.append("status", status);

        if (normalizedUploadedBy)
          params.append("uploaded_by", normalizedUploadedBy);

        if (holdingDate)
          params.append("holding_date", dayjs(holdingDate).format("YYYY-MM-DD"));

        if (normalizedFileName)
          params.append("file_name", normalizedFileName);

        if (fromDate)
          params.append("from_date", dayjs(fromDate).format("YYYY-MM-DD"));

        if (toDate)
          params.append("to_date", dayjs(toDate).format("YYYY-MM-DD"));

        const response = await fetch(
          `${CAREPMS_UPLOAD_HISTORY_ENDPOINT}?${params.toString()}`,
          { headers: { "Content-Type": "application/json", "X-Tenant-Id": tenant } }
        );
        if (!response.ok) throw new Error("Failed to fetch data");
        const jsonData = await response.json();
        setUploadHistoryData(jsonData.results);
        setRowCount(jsonData.count);
      } catch (error) {
        console.error("Error fetching data:", error.message);
      } finally {
        setLoading(false);
      }
    };

    if (shouldResetPage) {
      setController((prev) => ({ ...prev, page: 0 }));
      return;
    }

    fetchData();
  }, [
    controller.page,
    controller.rowsPerPage,
    refreshKey,
    refreshTick,
    dateInput,
    fileType,
    status,
    uploadedBy,
    holdingDate,
    fileName,
    fromDate,
    toDate,
  ]);

  const handlePageChange        = (event, newPage) => setController((prev) => ({ ...prev, page: newPage }));
  const handleChangeRowsPerPage = (event) => setController({ page: 0, rowsPerPage: parseInt(event.target.value, 10) });

  const handleDeleteClick = (row) => {
    setSelectedRowForDelete(row);
    setDeleteModalOpen(true);
  };

  const handleDeleteModalClose = () => {
    setDeleteModalOpen(false);
    setSelectedRowForDelete(null);
  };

  const handleDeleteSuccess = () => {
    setSnackbar({ open: true, message: "File deleted successfully.", severity: "success" });
    setSelectedRowForDelete(null);
    setRefreshTick((tick) => tick + 1);
  };

  const handleDeleteError = () => {
    setSnackbar({
      open: true,
      message: "Unable to delete the file. Please try again or contact your administrator.",
      severity: "error",
    });
    setSelectedRowForDelete(null);
  };

  const handleSnackbarClose = () => setSnackbar((prev) => ({ ...prev, open: false }));

  return (
    <>
      <AppTable
        columns={getTableColumns(handleDeleteClick, isAdmin)}
        rows={uploadHistoryData}
        rowKey={(data, index) => index}
        loading={loading}
        emptyMessage="No match found"
        maxHeight={530}
        count={rowCount}
        page={controller.page}
        rowsPerPage={controller.rowsPerPage}
        rowsPerPageOptions={[20, 50, 100]}
        onPageChange={handlePageChange}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />

      <UploadHistoryDeleteModal
        open={deleteModalOpen}
        handleClose={handleDeleteModalClose}
        selectedRow={selectedRowForDelete}
        onSuccess={handleDeleteSuccess}
        onError={handleDeleteError}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbar.severity} sx={{ width: "100%" }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
}